import { Vec3 } from '@/core/math/Vec3';
import { CelestialBody } from '@/universe/CelestialBody';
import { elementsFromState, stateVectorAt } from '@/universe/OrbitalElements';
import { Spacecraft } from '@/spacecraft/Spacecraft';

export type WarpMode = 'realtime' | 'physics' | 'analytical';

/**
 * Available time-acceleration rates.
 *
 * Rates up to 10x run the full physics simulation with sub-stepping. Beyond
 * that the vessel is propagated analytically, because integrating a hundred
 * thousand 1/60-second steps per rendered frame is not possible and pretending
 * otherwise would just produce a badly integrated orbit.
 */
export const WARP_RATES = [1, 2, 5, 10, 50, 100, 1000, 10000, 100000] as const;

/** Highest rate that still runs full rigid-body physics. */
export const MAX_PHYSICS_WARP = 10;

export interface WarpRestriction {
  /** Highest rate currently permitted. */
  maxRate: number;
  /** Why the rate is limited, for display. */
  reason: string | null;
}

export interface AutoStopConfig {
  /** Drop to real time this many seconds before a manoeuvre node. */
  beforeManeuver: number;
  /** Drop out of warp when the atmosphere is this many metres away. */
  beforeAtmosphere: number;
  /** Drop out of warp when terrain is this close. */
  beforeTerrain: number;
  /** Drop out when a sphere-of-influence change is imminent. */
  onSoiChange: boolean;
  /** Drop out when propellant falls below this fraction. */
  fuelFraction: number;
  /** Drop out when battery charge falls below this fraction. */
  powerFraction: number;
  /** Drop out when the hull exceeds this fraction of its temperature limit. */
  thermalFraction: number;
}

export function defaultAutoStop(): AutoStopConfig {
  return {
    beforeManeuver: 300,
    beforeAtmosphere: 20000,
    beforeTerrain: 5000,
    onSoiChange: true,
    fuelFraction: 0.05,
    powerFraction: 0.1,
    thermalFraction: 0.85,
  };
}

export interface WarpContext {
  ship: Spacecraft;
  referenceBody: CelestialBody;
  /** Altitude above terrain, metres. */
  altitude: number;
  /** Altitude above the reference datum, metres. */
  altitudeAboveDatum: number;
  /** Air density at the vessel, kg/m^3. */
  density: number;
  /** Seconds until the next manoeuvre node, or Infinity. */
  timeToManeuver: number;
  /** Seconds until a predicted sphere-of-influence change, or Infinity. */
  timeToSoiChange: number;
  /** True while any engine is producing thrust. */
  thrusting: boolean;
  /** True when landed or in contact with a surface. */
  landed: boolean;
}

export interface WarpStepResult {
  /**
   * The vessel is parked and time is running faster than physics can follow, so
   * the caller must carry it with the rotating surface instead of integrating or
   * propagating it. See `debugUnrestricted`.
   */
  surfaceHold: boolean;
  /** Simulation seconds the caller should advance. */
  simulationDelta: number;
  /** Number of full physics sub-steps to run. */
  physicsSteps: number;
  /** True when the vessel was moved analytically instead of integrated. */
  analytical: boolean;
  /** Set when the warp rate was reduced automatically this step. */
  autoStopReason: string | null;
}

/**
 * Time acceleration.
 *
 * Two mechanisms, one interface:
 *
 *   Physics warp (up to 10x) runs the ordinary simulation more times per
 *   rendered frame. Thrust, gravity, attitude control, propellant consumption,
 *   heating and collision all behave normally, just faster.
 *
 *   Analytical warp (10x and above) replaces the vessel's numerical integration
 *   with Keplerian propagation about the dominant body. It is only permitted
 *   when that substitution is actually valid: no thrust, no atmosphere, no
 *   surface contact, nothing about to happen. Those are not arbitrary
 *   restrictions, they are the exact conditions under which a two-body
 *   analytical solution and the full simulation agree.
 *
 * The controller never bypasses a constraint. If a vessel would run out of
 * propellant, overheat, hit terrain or change sphere of influence, the warp
 * drops before the event rather than skipping over it.
 */
export class TimeWarpController {
  private rateIndex = 0;
  readonly autoStop: AutoStopConfig = defaultAutoStop();
  /** Reason the rate was last reduced, cleared when the player changes rate. */
  lastAutoStopReason: string | null = null;

  /**
   * Lifts the situational rate limits. Debug only.
   *
   * The limits below are not arbitrary — each marks a condition under which the
   * cheaper propagation and the full simulation stop agreeing. But one of them
   * blocks something entirely reasonable: parked on a surface, warp is capped at
   * 10x, and on a world with a long day that makes watching a sunrise take real
   * hours. Vesper's day is 84 hours, Ashkar's 241.
   *
   * The vessel is not put at risk by lifting it, because the caller does not
   * propagate a parked vessel at all — see `surfaceHold`. What is given up is
   * that nothing which needs physics can happen while it runs.
   */
  debugUnrestricted = false;

  get rate(): number {
    return WARP_RATES[this.rateIndex];
  }

  get index(): number {
    return this.rateIndex;
  }

  get mode(): WarpMode {
    if (this.rate === 1) return 'realtime';
    return this.rate <= MAX_PHYSICS_WARP ? 'physics' : 'analytical';
  }

  /** Attempts to raise the warp rate one step. Returns the new rate. */
  increase(context: WarpContext): number {
    const restriction = this.restriction(context);
    const next = Math.min(this.rateIndex + 1, WARP_RATES.length - 1);
    if (WARP_RATES[next] <= restriction.maxRate) {
      this.rateIndex = next;
      this.lastAutoStopReason = null;
    } else {
      this.lastAutoStopReason = restriction.reason;
    }
    return this.rate;
  }

  decrease(): number {
    this.rateIndex = Math.max(0, this.rateIndex - 1);
    this.lastAutoStopReason = null;
    return this.rate;
  }

  reset(): void {
    this.rateIndex = 0;
    this.lastAutoStopReason = null;
  }

  setRate(rate: number, context: WarpContext): number {
    const restriction = this.restriction(context);
    const allowed = Math.min(rate, restriction.maxRate);
    let best = 0;
    for (let i = 0; i < WARP_RATES.length; i++) {
      if (WARP_RATES[i] <= allowed) best = i;
    }
    this.rateIndex = best;
    return this.rate;
  }

  /**
   * The highest rate currently permitted, and why.
   *
   * Note the ordering: the most restrictive condition wins, and the reason
   * reported is the one that produced the limit.
   */
  restriction(context: WarpContext): WarpRestriction {
    const { ship, referenceBody } = context;

    // Debug override. Deliberately placed first: the point is to reach rates the
    // conditions below exist to prevent.
    if (this.debugUnrestricted) {
      return { maxRate: WARP_RATES[WARP_RATES.length - 1], reason: null };
    }

    if (context.landed) {
      // On the ground the vessel is in contact with terrain, so analytical
      // propagation is meaningless. Physics warp is still fine.
      return { maxRate: MAX_PHYSICS_WARP, reason: 'Landed: analytical warp unavailable while in surface contact' };
    }

    if (context.thrusting) {
      return { maxRate: MAX_PHYSICS_WARP, reason: 'Engines running: analytical warp would ignore thrust' };
    }

    if (context.density > 1e-7) {
      const rate = context.density > 1e-4 ? 2 : MAX_PHYSICS_WARP;
      return { maxRate: rate, reason: 'In atmosphere: drag must be simulated' };
    }

    if (context.altitude < this.autoStop.beforeTerrain) {
      return { maxRate: 2, reason: 'Close to terrain' };
    }

    if (context.altitudeAboveDatum < referenceBody.atmosphereTop + this.autoStop.beforeAtmosphere) {
      return { maxRate: 50, reason: 'Approaching atmospheric interface' };
    }

    if (context.timeToManeuver < this.autoStop.beforeManeuver) {
      const rate = context.timeToManeuver < 30 ? 1 : 10;
      return { maxRate: rate, reason: 'Manoeuvre node approaching' };
    }

    if (this.autoStop.onSoiChange && context.timeToSoiChange < 120) {
      return { maxRate: 10, reason: 'Sphere-of-influence transition approaching' };
    }

    const tel = ship.telemetry();
    if (tel.propellantFraction < this.autoStop.fuelFraction) {
      return { maxRate: 100, reason: 'Propellant reserve low' };
    }
    if (ship.power.chargeFraction < this.autoStop.powerFraction) {
      return { maxRate: 100, reason: 'Battery charge low' };
    }
    if (tel.hullTemperature / ship.thermal.temperatureLimit > this.autoStop.thermalFraction) {
      return { maxRate: 1, reason: 'Hull temperature critical' };
    }

    return { maxRate: WARP_RATES[WARP_RATES.length - 1], reason: null };
  }

  /**
   * Called once per rendered frame. Clamps the rate against current conditions,
   * then reports how the caller should advance the simulation.
   */
  step(realDelta: number, context: WarpContext, physicsStep: number): WarpStepResult {
    const restriction = this.restriction(context);
    let autoStopReason: string | null = null;

    if (this.rate > restriction.maxRate) {
      let best = 0;
      for (let i = 0; i < WARP_RATES.length; i++) {
        if (WARP_RATES[i] <= restriction.maxRate) best = i;
      }
      this.rateIndex = best;
      autoStopReason = restriction.reason;
      this.lastAutoStopReason = autoStopReason;
    }

    const rate = this.rate;
    const simulationDelta = realDelta * rate;

    // A parked vessel above the physics rate is the one case that must not take
    // either normal path. Analytical propagation would put a Keplerian orbit
    // through a vessel sitting on the ground and launch it; full physics would
    // ask for thousands of sub-steps per frame and stall. It is carried with the
    // rotating surface instead, which is what contact and friction were doing
    // anyway.
    if (rate > MAX_PHYSICS_WARP && context.landed) {
      return {
        surfaceHold: true,
        simulationDelta,
        physicsSteps: 0,
        analytical: false,
        autoStopReason,
      };
    }

    if (rate <= MAX_PHYSICS_WARP) {
      return {
        surfaceHold: false,
        simulationDelta,
        physicsSteps: Math.round(simulationDelta / physicsStep),
        analytical: false,
        autoStopReason,
      };
    }

    return {
      surfaceHold: false,
      simulationDelta,
      physicsSteps: 0,
      analytical: true,
      autoStopReason,
    };
  }

  /**
   * Propagates the vessel analytically over `dt` seconds.
   *
   * Returns the new state in the reference body's inertial frame. Attitude is
   * carried forward by the vessel's existing angular velocity, so a slowly
   * tumbling ship is still tumbling when it comes out of warp.
   */
  propagateAnalytically(
    ship: Spacecraft,
    referenceBody: CelestialBody,
    t: number,
    dt: number,
  ): { position: Vec3; velocity: Vec3 } {
    const elements = elementsFromState(ship.body.position, ship.body.velocity, referenceBody.mu, t);
    const sv = stateVectorAt(elements, t + dt);
    return sv;
  }
}
